/**
 * Разделы «Продажи» и «Склад» — описание, адреса и права.
 *
 * Модуль самодостаточен: навигация берёт отсюда список пунктов, маршруты —
 * отсюда же адреса. Чтобы переложить разделы в другое место меню, достаточно
 * перенести <InsightsNav /> или вызвать insightsNavGroups() в новой навигации.
 *
 * Раздел виден, только если выдан отчёт, на котором он построен, и раздел
 * не скрыт администратором; список приходит с сервера в user.sections.
 * Блок внутри раздела, чей отчёт закрыт, объясняет это сам.
 */

import type { CurrentUser } from '../types'
import type { IconName } from '../components/Icon'

export type InsightsGroup = 'sales' | 'stock'
export type SalesSection = 'managers' | 'customers' | 'channels'
export type StockSection = 'stock' | 'turnover'

export interface InsightsSection<T extends string> {
  code: T
  title: string
  short: string
  icon: IconName
  subtitle: string
}

export const SALES_SECTIONS: InsightsSection<SalesSection>[] = [
  { code: 'managers', title: 'Менеджеры', short: 'Менеджеры', icon: 'user', subtitle: 'Выручка, валовая прибыль и клиенты по менеджерам' },
  { code: 'customers', title: 'Клиенты', short: 'Клиенты', icon: 'users', subtitle: 'Концентрация выручки, крупнейшие клиенты, кто пришёл и кто перестал покупать' },
  { code: 'channels', title: 'Каналы и юрлица', short: 'Каналы и юрлица', icon: 'channels', subtitle: 'Выручка по каналам, организациям группы и типу клиента' },
]

export const STOCK_SECTIONS: InsightsSection<StockSection>[] = [
  { code: 'stock', title: 'Запасы', short: 'Запасы', icon: 'stock', subtitle: 'Сколько денег лежит на складах и что не продаётся' },
  { code: 'turnover', title: 'Оборачиваемость', short: 'Оборачиваемость', icon: 'turnover', subtitle: 'Дни запаса и отдача на рубль запаса по месяцам и позициям' },
]

// Видимость считает сервер (src/auth/sections.ts): выданные отчёты,
// скрытые показатели и скрытые администратором разделы.
const on = (user: CurrentUser, code: string) => (user.sections ?? []).includes(code)

export function salesAccess(user: CurrentUser): Record<SalesSection, boolean> {
  return {
    managers: on(user, 'sales.managers'),
    customers: on(user, 'sales.customers'),
    channels: on(user, 'sales.channels'),
  }
}

export function stockAccess(user: CurrentUser): Record<StockSection, boolean> {
  return {
    stock: on(user, 'stock.stock'),
    turnover: on(user, 'stock.turnover'),
  }
}

export const visibleSalesSections = (user: CurrentUser) => SALES_SECTIONS.filter((section) => salesAccess(user)[section.code])
export const visibleStockSections = (user: CurrentUser) => STOCK_SECTIONS.filter((section) => stockAccess(user)[section.code])

export const salesPath = (code: SalesSection) => `/sales/${code}`
export const stockPath = (code: StockSection) => `/stock/${code}`

/** Группы для навигации: заголовок и пункты. Пустые группы не возвращаются. */
export function insightsNavGroups(user: CurrentUser): Array<{ group: InsightsGroup; title: string; items: Array<{ path: string; short: string; icon: IconName }> }> {
  const groups = [
    { group: 'sales' as const, title: 'Продажи', items: visibleSalesSections(user).map((item) => ({ path: salesPath(item.code), short: item.short, icon: item.icon })) },
    { group: 'stock' as const, title: 'Склад', items: visibleStockSections(user).map((item) => ({ path: stockPath(item.code), short: item.short, icon: item.icon })) },
  ]
  return groups.filter((group) => group.items.length > 0)
}
